import { FC, useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Grid,
} from '@mui/material';
import { CompareArrows, ArrowBack } from '@mui/icons-material';
import { api } from '@/services/api';
import { ComparisonSummary } from '@/components/Comparison/ComparisonSummary';
import { InterfaceComparison } from '@/components/Comparison/InterfaceComparison';
import { VlanComparison } from '@/components/Comparison/VlanComparison';
import type {
  ComparisonResult,
  VersionSummary,
  SwitchVersionsResponse,
} from '@/types/entities';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
};

export const ComparisonView: FC = () => {
  const { hostname } = useParams<{ hostname: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [versionsData, setVersionsData] = useState<SwitchVersionsResponse | null>(null);
  const [selectedVersionA, setSelectedVersionA] = useState<string>('');
  const [selectedVersionB, setSelectedVersionB] = useState<string>('');
  const [comparisonResult, setComparisonResult] = useState<ComparisonResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);

  // Fetch available versions for the switch
  useEffect(() => {
    if (!hostname) return;

    const fetchVersions = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await api.getSwitchVersions(hostname);
        setVersionsData(data);

        // Check URL parameters for pre-selection
        const urlVersionA = searchParams.get('versionA');
        const urlVersionB = searchParams.get('versionB');

        if (urlVersionA) {
          setSelectedVersionA(urlVersionA);
        } else if (data.versions.length >= 2) {
          // Default: second most recent
          const sorted = [...data.versions].sort(
            (a, b) => new Date(b.uploadTimestamp).getTime() - new Date(a.uploadTimestamp).getTime()
          );
          setSelectedVersionA(sorted[1].transactionId);
        }

        if (urlVersionB) {
          setSelectedVersionB(urlVersionB);
        } else if (data.versions.length >= 2) {
          // Default: most recent
          const sorted = [...data.versions].sort(
            (a, b) => new Date(b.uploadTimestamp).getTime() - new Date(a.uploadTimestamp).getTime()
          );
          setSelectedVersionB(sorted[0].transactionId);
        }
      } catch (err) {
        setError('Failed to load switch versions');
        console.error('Error loading versions:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchVersions();
  }, [hostname, searchParams]);

  // Auto-run comparison if autorun=true in URL params
  useEffect(() => {
    const autorun = searchParams.get('autorun');
    if (autorun === 'true' && selectedVersionA && selectedVersionB && !comparisonResult && !loading) {
      handleCompare();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVersionA, selectedVersionB, searchParams]);

  const handleCompare = async () => {
    if (!hostname || !selectedVersionA || !selectedVersionB) return;

    if (selectedVersionA === selectedVersionB) {
      setError('Please select two different versions to compare');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const result = await api.compareVersions({
        hostname,
        transactionIdA: selectedVersionA,
        transactionIdB: selectedVersionB,
      });
      setComparisonResult(result);
      setActiveTab(0); // Reset to summary tab
    } catch (err) {
      setError('Failed to compare versions');
      console.error('Error comparing versions:', err);
    } finally {
      setLoading(false);
    }
  };

  const getVersionLabel = (version: VersionSummary): string => {
    const date = new Date(version.uploadTimestamp).toLocaleDateString();
    const time = new Date(version.uploadTimestamp).toLocaleTimeString();
    return `${date} ${time} - ${version.filename}`;
  };

  if (!hostname) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">No switch hostname provided</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate(`/switch/${hostname}`)}
        >
          Back to Switch
        </Button>
        <Typography variant="h4">
          Configuration Comparison: {hostname}
        </Typography>
      </Box>

      {/* Version Selectors */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Select Versions to Compare
        </Typography>

        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={12} md={5}>
            <FormControl fullWidth disabled={loading || !versionsData}>
              <InputLabel>Version A (Baseline)</InputLabel>
              <Select
                value={selectedVersionA}
                onChange={(e) => setSelectedVersionA(e.target.value)}
                label="Version A (Baseline)"
              >
                {versionsData?.versions.map((version) => (
                  <MenuItem key={version.transactionId} value={version.transactionId}>
                    {getVersionLabel(version)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CompareArrows fontSize="large" color="action" />
          </Grid>

          <Grid item xs={12} md={5}>
            <FormControl fullWidth disabled={loading || !versionsData}>
              <InputLabel>Version B (Comparison)</InputLabel>
              <Select
                value={selectedVersionB}
                onChange={(e) => setSelectedVersionB(e.target.value)}
                label="Version B (Comparison)"
              >
                {versionsData?.versions.map((version) => (
                  <MenuItem key={version.transactionId} value={version.transactionId}>
                    {getVersionLabel(version)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="contained"
            size="large"
            onClick={handleCompare}
            disabled={loading || !selectedVersionA || !selectedVersionB || selectedVersionA === selectedVersionB}
            startIcon={loading ? <CircularProgress size={20} /> : <CompareArrows />}
          >
            {loading ? 'Comparing...' : 'Compare Versions'}
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </Paper>

      {/* Comparison Results */}
      {comparisonResult && (
        <Paper sx={{ mt: 3 }}>
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="Summary" />
            <Tab label="Interfaces" />
            <Tab label="VLANs" />
          </Tabs>

          {/* Summary Tab */}
          <TabPanel value={activeTab} index={0}>
            <ComparisonSummary comparison={comparisonResult} />
          </TabPanel>

          {/* Interfaces Tab */}
          <TabPanel value={activeTab} index={1}>
            <InterfaceComparison comparison={comparisonResult} />
          </TabPanel>

          {/* VLANs Tab */}
          <TabPanel value={activeTab} index={2}>
            <VlanComparison comparison={comparisonResult} />
          </TabPanel>
        </Paper>
      )}
    </Box>
  );
};

export default ComparisonView;
